import { firestore, timestamp } from "@/api/firebase/firebase.config";
import { Query_Imgix } from "@/api/imgix";
import { useCurrentUserProfile } from "@/store/auth/userProfile.store";
import { brand } from "branchAndBrandSpecific/utils/brandVariables/brandVariables";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { NewsProps } from "../types/NewsTypes";
import validateSubmitNewsForm from "../helperFunctions/validateSubmitNewsForm";

const useSubmitNews = (data: NewsProps) => {
  const currentUser = useCurrentUserProfile((s) => s.user);
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [success, setSuccess] = useState(false);
  const [values, setValues] = useState<NewsProps>({
    title: data?.title || "",
    slug: data?.slug || "",
    intro: data?.intro || "",
    content: data?.content || "",
    imageURL: data?.imageURL || "",
    client: brand.tenantId,
    created: timestamp.now(),
    createdBy: currentUser.uid,
  });

  useEffect(() => {
    if (data) {
      setValues({
        ...values,
        ...data,
      });
    }
  }, [data]);

  const submitNews = async (values) => {
    setLoading(true);
    setError(null);
    setSuccess(false);

    const validationErrors = await validateSubmitNewsForm(values);
    if (validationErrors) {
      const firstError = Object.values(validationErrors)[0];
      setError(new Error(firstError));
      setLoading(false);
      return;
    }

    try {
      let firebaseFileURL: string | null = null;

      if (values.imageURL instanceof Blob || values.imageURL instanceof File) {
        firebaseFileURL = await Query_Imgix.convertImageToImgIX(
          values.imageURL,
          `${brand.tenantId}/news/`
        );
      } else if (typeof values.imageURL === "string") {
        firebaseFileURL = values.imageURL;
      }

      const updatedValues = {
        ...values,
        imageURL: firebaseFileURL,
      };

      const newsRef = firestore
        .collection("news")
        .doc(brand.tenantId)
        .collection("articles")
        .doc(values.slug);

      await newsRef.get().then((doc) => {
        if (!doc.exists) {
          newsRef.set(updatedValues).then(() => {
            setSuccess(true);
            navigate(`/news/${values.slug}`);
          });
        } else {
          newsRef.update(updatedValues).then(() => {
            setSuccess(true);
            navigate(`/news/${values.slug}`);
          });
        }
      });
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  return { submitNews, loading, error, success };
};

export default useSubmitNews;
